import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Crown, 
  ExternalLink, 
  CheckCircle, 

  ArrowRight,
  SkipForward
} from 'lucide-react';
import { validateChessUsername } from '@/lib/utils';

export const ChessSetup: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [chessUsername, setChessUsername] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Debug logging
  console.log('ChessSetup component rendered', { user });

  // Add a simple test to verify the page is accessible
  useEffect(() => {
    console.log('ChessSetup page loaded successfully');
  }, []);

  const initMutation = useMutation(
    (data: { gasUserId: string; chessUsername: string; email: string; name: string }) => 
      apiClient.initUser(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userProfile');
        queryClient.invalidateQueries('leaderboard');
        queryClient.invalidateQueries('allMembers');
        navigate('/profile?setup=complete');
      },
      onError: (error: any) => {
        setValidationError(error.response?.data?.error || 'Failed to initialize profile');
      }
    }
  );

  const connectMutation = useMutation(
    (data: { chessUsername: string }) => 
      apiClient.connectChessProfile(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userProfile');
        queryClient.invalidateQueries('leaderboard');
        queryClient.invalidateQueries('allMembers');
        navigate('/profile?setup=complete');
      },
      onError: (error: any) => {
        setValidationError(error.response?.data?.error || 'Failed to connect Chess.com profile');
      }
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!chessUsername.trim()) {
      setValidationError('Please enter a Chess.com username');
      return;
    }

    if (!validateChessUsername(chessUsername)) {
      setValidationError('Invalid Chess.com username format');
      return;
    }

    if (!user) {
      setValidationError('User information not available');
      return;
    }

    if (!user.gasUserId) {
      setValidationError('User ID not available. Please refresh the page and try again.');
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      // Validate the username exists on Chess.com
      const isValid = await apiClient.validateChessUsername(chessUsername);
      
      if (!isValid) {
        setValidationError('Chess.com username not found. Please check the username or create a new account.');
        return;
      }

      // Check if user already exists
      const userProfile = await apiClient.getUserProfile().catch(() => null);
      
      if (userProfile?.data) {
        // User exists, connect Chess.com profile
        connectMutation.mutate({
          chessUsername: chessUsername.trim(),
        });
      } else {
        // New user, initialize profile
        initMutation.mutate({
          gasUserId: user.gasUserId || '',
          chessUsername: chessUsername.trim(),
          email: user.email,
          name: user.name,
        });
      }
    } catch (error: any) {
      setValidationError(error.message || 'Failed to validate username');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSkip = async () => {
    if (!user) {
      setValidationError('User information not available');
      return;
    }

    if (!user.gasUserId) {
      setValidationError('User ID not available. Please refresh the page and try again.');
      return;
    }

    setIsSkipping(true);
    setValidationError(null);

    try {
      // Check if user already exists
      const userProfile = await apiClient.getUserProfile().catch(() => null);
      
      if (userProfile?.data) {
        // User already exists, just navigate back
        console.log('User already exists, skipping to profile');
        navigate('/profile?skipped=true');
      } else {
        // User doesn't exist, create them without Chess.com profile
        console.log('Creating new user without Chess.com profile');
        await apiClient.createUserWithoutChess({
          gasUserId: user.gasUserId,
          email: user.email,
          name: user.name,
        });
        
        // Invalidate queries and navigate
        queryClient.invalidateQueries('userProfile');
        queryClient.invalidateQueries('allMembers');
        navigate('/profile?skipped=true');
      }
    } catch (error: any) {
      setValidationError(error.response?.data?.error || 'Failed to create profile');
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <Crown className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome to ChessHub!</h1>
          <p className="text-gray-600 mt-2">
            Connect your Chess.com account to join the leaderboard and compete with your colleagues.
          </p>
        </div>

        {/* Setup Card */}
        <Card>
          <CardHeader>
            <CardTitle>Connect Your Chess.com Account</CardTitle>
            <p className="text-sm text-gray-600">
              Enter your existing Chess.com username or create a new account to get started.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  label="Chess.com Username"
                  placeholder="Enter your Chess.com username"
                  value={chessUsername}
                  onChange={(e) => {
                    setChessUsername(e.target.value);
                    setValidationError(null);
                  }}
                  error={validationError || undefined}
                  helperText="3-20 characters, letters, numbers, and underscores only"
                />
              </div>

              {/* User Info Display */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm text-gray-600">
                  <div><strong>Name:</strong> {user?.name}</div>
                  <div><strong>Email:</strong> {user?.email}</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  type="submit"
                  loading={isValidating || initMutation.isLoading || connectMutation.isLoading}
                  disabled={!chessUsername.trim() || isValidating || initMutation.isLoading || connectMutation.isLoading}
                  className="w-full"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Connect Account
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSkip}
                  loading={isSkipping}
                  disabled={isSkipping || isValidating || initMutation.isLoading || connectMutation.isLoading}
                  className="w-full"
                >
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip for Now
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card>
          <CardHeader>
            <CardTitle>Need a Chess.com Account?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Don't have a Chess.com account yet? Create one for free and start playing chess online!
            </p>
            
            <div className="space-y-3">
              <a
                href="https://www.chess.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-2 w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Create Chess.com Account</span>
                <ArrowRight className="h-4 w-4" />
              </a>
              
              <div className="text-xs text-gray-500 text-center">
                After creating your account, return here and enter your username.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card>
          <CardHeader>
            <CardTitle>Why Connect Your Chess.com Account?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Join the company leaderboard and compete with colleagues</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Track your progress across different time controls</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Share your achievements in the community</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Get recognized with badges and rankings</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}; 